/**
 * TimeOffModal — add or edit time-off blocks for a single tech.
 *
 * Persists by mutating the tech's `time_off` array on the technician
 * record (existing schema) — no new collection or endpoints required.
 * Each block carries an `id` so we can target individual blocks for
 * edit/delete without index drift.
 *
 * Multi-category: the category picker is a checkbox list. Every ticked
 * category is written across the full start→end range, so a multi-day
 * selection gets all chosen categories on every day.
 *
 * Conflict guard: caller supplies `existingDispatchDates` (a Set of
 * yyyy-MM-dd strings the tech is dispatched on). If the chosen range
 * overlaps any of them we surface a warning but still let the user
 * proceed — the dispatcher decides whether to remove dispatches first.
 */
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { updateTechnician } from "@/lib/api";
import { useDispatchCategories, findCategory } from "@/lib/dispatchCategories";
import { CategoryChecklist } from "@/components/CategoryChecklist";

function newId() {
  return "to_" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);
}

export default function TimeOffModal({ open, onOpenChange, tech, initialDate, blockId, defaultReason, onSaved, existingDispatchDates }) {
  const categories = useDispatchCategories();
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selected, setSelected] = useState([]);
  const [details, setDetails] = useState({});
  const [otherOn, setOtherOn] = useState(false);
  const [otherNote, setOtherNote] = useState("");
  const [saving, setSaving] = useState(false);

  const editing = !!blockId;
  const block = editing ? (tech?.time_off || []).find(t => t.id === blockId) : null;

  // Legacy time_off entries may not have an `id` — back-fill them on
  // save so future edits target the right block.
  const findIdx = () => {
    const arr = tech?.time_off || [];
    let idx = arr.findIndex(t => t.id === blockId);
    if (idx >= 0) return idx;
    if (block) idx = arr.findIndex(t => t.start_date === block.start_date && t.end_date === block.end_date && t.reason === block.reason);
    return idx;
  };

  useEffect(() => {
    if (!open) return;
    if (block) {
      setStartDate(block.start_date || "");
      setEndDate(block.end_date || block.start_date || "");
      const known = categories.map(c => c.label);
      if (known.includes(block.reason)) {
        setSelected([block.reason]);
        setOtherOn(false);
        setOtherNote("");
      } else {
        setSelected([]);
        setOtherOn(true);
        setOtherNote(block.reason || "");
      }
      setDetails({ [block.reason]: { hours: typeof block.hours === "number" ? block.hours : 10, notes: block.notes || "" } });
    } else {
      setStartDate(initialDate || "");
      setEndDate(initialDate || "");
      const seed = defaultReason && categories.some(c => c.label === defaultReason)
        ? defaultReason
        : (categories[0]?.label || null);
      setSelected(seed ? [seed] : []);
      setOtherOn(false);
      setOtherNote("");
      setDetails({});
    }
  }, [open, block, initialDate, defaultReason, categories]);

  const toggle = (label) => setSelected(s => (s.includes(label) ? s.filter(l => l !== label) : [...s, label]));
  const onDetailChange = (label, patch) => setDetails(d => ({ ...d, [label]: { hours: 10, notes: "", ...(d[label] || {}), ...patch } }));

  const overlapping = (() => {
    if (!startDate || !endDate || !existingDispatchDates?.size) return [];
    const conflicts = [];
    let d = new Date(startDate + "T12:00:00");
    const end = new Date(endDate + "T12:00:00");
    while (d <= end) {
      const s = d.toISOString().slice(0, 10);
      if (existingDispatchDates.has(s) && (!block || !block.start_date || s < block.start_date || s > (block.end_date || block.start_date))) {
        conflicts.push(s);
      }
      d.setDate(d.getDate() + 1);
    }
    return conflicts;
  })();

  const persist = async (nextArr) => {
    setSaving(true);
    try {
      await updateTechnician(tech.id, { time_off: nextArr });
      toast.success(editing ? "Time off updated" : "Time off added");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error("Save failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const buildBlock = (label) => {
    const cat = findCategory(categories, label);
    const extras = cat?.capture_details
      ? { hours: parseFloat(details[label]?.hours) || 10, notes: details[label]?.notes || "" }
      : {};
    return { start_date: startDate, end_date: endDate, reason: label, category_id: cat?.id || null, ...extras };
  };

  const handleSave = async () => {
    if (!startDate || !endDate) { toast.error("Pick start and end dates"); return; }
    if (endDate < startDate) { toast.error("End date can't be before start"); return; }
    const labels = [...selected];
    if (otherOn) labels.push(otherNote.trim() || "Other");
    if (!labels.length) { toast.error("Pick at least one category"); return; }

    const arr = [...(tech?.time_off || [])];
    if (editing) {
      const idx = findIdx();
      const [primary, ...rest] = labels;
      if (idx >= 0) arr[idx] = { ...arr[idx], id: arr[idx].id || newId(), ...buildBlock(primary) };
      rest.forEach(l => arr.push({ id: newId(), ...buildBlock(l) }));
    } else {
      labels.forEach(l => arr.push({ id: newId(), ...buildBlock(l) }));
    }
    persist(arr);
  };

  const handleDelete = async () => {
    if (!editing) return;
    if (!window.confirm(`Remove this ${block?.reason || "time off"} block?`)) return;
    const arr = (tech?.time_off || []).filter(t => t.id !== blockId);
    persist(arr);
  };

  const multiDay = startDate && endDate && startDate !== endDate;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]" data-testid="time-off-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: tech?.color || "#cbd5e1" }} />
            {editing ? "Edit Time Off" : "Add Time Off"}
            <span className="text-sm font-normal text-slate-500">— {tech?.name}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Start</Label>
              <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="h-9" data-testid="time-off-start" />
            </div>
            <div>
              <Label className="text-xs">End</Label>
              <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="h-9" data-testid="time-off-end" />
            </div>
          </div>

          <div>
            <Label className="text-xs">Categories</Label>
            <div className="mt-1 rounded-md border border-slate-200 overflow-hidden" data-testid="time-off-reason">
              <CategoryChecklist
                categories={categories}
                selected={selected}
                details={details}
                onToggle={toggle}
                onDetailChange={onDetailChange}
                maxHeight={220}
                testPrefix="time-off-cat"
              />
              <button
                type="button"
                onClick={() => setOtherOn(v => !v)}
                className={`w-full flex items-center gap-2 px-3 py-1.5 text-sm border-t border-slate-100 hover:bg-slate-50 ${otherOn ? "bg-slate-50 font-medium" : ""}`}
                data-testid="time-off-cat-other"
              >
                <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${otherOn ? "bg-slate-900 border-slate-900" : "border-slate-300 bg-white"}`}>
                  {otherOn && <span className="w-2 h-2 bg-white rounded-sm" />}
                </span>
                <span className="flex-1 text-left">Other</span>
              </button>
            </div>
            {multiDay && selected.length + (otherOn ? 1 : 0) > 1 && (
              <p className="text-[10px] text-slate-500 mt-1" data-testid="time-off-multi-hint">
                All {selected.length + (otherOn ? 1 : 0)} categories will be applied to every day from {startDate} to {endDate}.
              </p>
            )}
          </div>

          {otherOn && (
            <div>
              <Label className="text-xs">Describe</Label>
              <Input value={otherNote} onChange={e => setOtherNote(e.target.value)} placeholder="Bereavement, jury duty…" className="h-9" data-testid="time-off-other-note" />
            </div>
          )}

          {overlapping.length > 0 && (
            <div className="flex items-start gap-2 px-3 py-2 rounded bg-amber-50 border border-amber-200 text-xs text-amber-800" data-testid="time-off-conflict">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <div>
                <div className="font-semibold">Overlaps {overlapping.length} dispatch{overlapping.length === 1 ? "" : "es"}</div>
                <div className="text-[11px] text-amber-700">{overlapping.slice(0, 5).join(", ")}{overlapping.length > 5 ? "…" : ""} — saving will mark these days off but won't remove the dispatches.</div>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between mt-2">
          <div>
            {editing && (
              <Button variant="outline" size="sm" onClick={handleDelete} disabled={saving} className="text-rose-600 hover:bg-rose-50" data-testid="time-off-delete">
                <Trash2 className="w-3.5 h-3.5 mr-1" />Remove
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
            <Button size="sm" onClick={handleSave} disabled={saving} data-testid="time-off-save">
              {saving ? "Saving…" : (editing ? "Update" : "Add Time Off")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
